import { Box, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { EditIcon } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import {
    AVAILABLE_ROI_TYPES,
    ROI_POINT_TYPES,
    RoiInterpretedType,
} from '~/domain/dicom/dicomstructureset';
import { RoiReadModel } from '~/domain/readmodels/StructureSetReadModel';
import { currentStructureSetState } from '~/state/atoms/structureSet/structureSetAtoms';
import { enqueueSnackbar } from '~/utils/notifications';

/**
 * Editable "interpreted type" control for a structure: shows the current type
 * and, when editable, a pencil that opens a menu of the allowed types for the
 * structure's category (point vs volumetric). Enforces the domain invariant
 * that a structure set may contain only one EXTERNAL (body contour) ROI — a
 * blocked choice is disabled in the menu and a warning is surfaced rather than
 * silently applied. This is the single canonical place type editing happens
 * (used by the docked structure summary for both point and volumetric ROIs).
 */
export default function InterpretedTypeField({
    roi,
    isEditable,
    onChange,
}: {
    roi: RoiReadModel;
    isEditable: boolean;
    onChange: (value: RoiInterpretedType) => void;
}) {
    const intl = useIntl();
    const currentStructureSet = useRecoilValue(currentStructureSetState);
    const [typeMenuAnchor, setTypeMenuAnchor] = useState<null | HTMLElement>(
        null
    );

    // Prevent switching between point and volumetric categories.
    const currentTypeIsPoint = roi.isPointRoi;

    // Only one EXTERNAL (body contour) is allowed per structure set.
    const existingExternalRoi = useMemo(
        () =>
            currentStructureSet?.roiArray.find(
                (r) =>
                    r.roiNumber !== roi.roiNumber &&
                    r.interpretedType === RoiInterpretedType.EXTERNAL
            ) ?? null,
        [currentStructureSet, roi.roiNumber]
    );

    const handleTypeChange = useCallback(
        (newType: RoiInterpretedType) => {
            if (
                newType === RoiInterpretedType.EXTERNAL &&
                existingExternalRoi
            ) {
                enqueueSnackbar(
                    `An EXTERNAL (${existingExternalRoi.name}) ` +
                        'structure already exists.',
                    { variant: 'warning' }
                );
                return;
            }
            onChange(newType);
        },
        [onChange, existingExternalRoi]
    );

    const allowedTypes = useMemo(() => {
        const categoryTypes = currentTypeIsPoint
            ? ROI_POINT_TYPES
            : AVAILABLE_ROI_TYPES.filter((t) => !ROI_POINT_TYPES.includes(t));

        return categoryTypes.includes(roi.interpretedType)
            ? categoryTypes
            : [roi.interpretedType, ...categoryTypes];
    }, [roi.interpretedType, currentTypeIsPoint]);

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={0.5}
            sx={{ whiteSpace: 'nowrap' }}
        >
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: 11, flexShrink: 0 }}
            >
                {intl.formatMessage({ id: 'structure.type' })}:
            </Typography>
            <Typography
                variant="caption"
                sx={{ fontSize: 11, flexShrink: 0 }}
                data-testid="structure-type-value"
            >
                {roi.interpretedType || intl.formatMessage({ id: 'unknown' })}
            </Typography>
            {isEditable && (
                <>
                    <Box
                        component="span"
                        onClick={(e) => setTypeMenuAnchor(e.currentTarget)}
                        sx={{
                            display: 'inline-flex',
                            flexShrink: 0,
                            cursor: 'pointer',
                            color: '#9CA3AF',
                            '&:hover': { color: 'primary.main' },
                        }}
                        data-testid="structure-type-edit-button"
                    >
                        <EditIcon size={10} />
                    </Box>
                    <Menu
                        anchorEl={typeMenuAnchor}
                        open={Boolean(typeMenuAnchor)}
                        onClose={() => setTypeMenuAnchor(null)}
                    >
                        {allowedTypes.map((value) => {
                            const isBlockedExternal =
                                value === RoiInterpretedType.EXTERNAL &&
                                roi.interpretedType !==
                                    RoiInterpretedType.EXTERNAL &&
                                existingExternalRoi !== null;

                            const item = (
                                <MenuItem
                                    key={value || 'UNKNOWN'}
                                    selected={value === roi.interpretedType}
                                    disabled={isBlockedExternal}
                                    onClick={() => {
                                        handleTypeChange(value);
                                        setTypeMenuAnchor(null);
                                    }}
                                >
                                    {value || 'Unknown'}
                                </MenuItem>
                            );

                            if (isBlockedExternal) {
                                return (
                                    <Tooltip
                                        key={value || 'UNKNOWN'}
                                        title={`An EXTERNAL (${existingExternalRoi?.name}) structure already exists`}
                                        placement="right"
                                    >
                                        <span>{item}</span>
                                    </Tooltip>
                                );
                            }
                            return item;
                        })}
                    </Menu>
                </>
            )}
        </Box>
    );
}
